import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/cms/ui/dialog";
import { UIImageUpload } from "@/components/cms/ui/ui-image-upload";
import { UIImageCategory, UI_IMAGE_CATEGORIES } from "@/modules/uiImages/types";

interface UIImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImageSelect: (imageUrl: string) => void;
  onCropApplied?: (data: {
    imageOriginalUrl: string;
    imageCroppedUrl: string;
    crop: { x: number; y: number; width: number; height: number }
  }) => void;
  currentImage?: string;
  currentOriginalImage?: string;
  title?: string;
  description?: string;
  enableCrop?: boolean;
  closeOnRemove?: boolean;
  imageCategory: UIImageCategory;
  projectInstanceId?: string;
  sceneOriginId?: string;
}

export function UIImageUploadDialog({
  open,
  onOpenChange,
  onImageSelect,
  onCropApplied,
  currentImage,
  currentOriginalImage,
  enableCrop = true,
  closeOnRemove = false,
  title,
  description,
  imageCategory,
  projectInstanceId,
  sceneOriginId
}: UIImageUploadDialogProps) {
  const categoryConfig = UI_IMAGE_CATEGORIES[imageCategory];

  const defaultTitle = title || `Upload ${imageCategory}`;
  const defaultDescription = description || `Select an image for ${categoryConfig.description.toLowerCase()}. This slot requires a ${categoryConfig.aspectRatio} aspect ratio image.`;

  const handleImageSelect = (imageUrl: string) => {

    onImageSelect(imageUrl);
    // Only close when a non-empty image is selected, or if instructed to close on remove
    if ((imageUrl && imageUrl.trim() !== '') || closeOnRemove) {

      onOpenChange(false);
    }
  };

  const handleCropApplied = (data: {
    imageOriginalUrl: string;
    imageCroppedUrl: string;
    crop: { x: number; y: number; width: number; height: number }
  }) => {
    onCropApplied?.(data);
    onOpenChange(false);
  };

  // If the upload component signals image removed (empty string), close dialog when requested
  const handleMaybeCloseOnRemove = (url: string) => {
    if (closeOnRemove && (!url || url.trim() === '')) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="cursor-default select-none">
        <DialogHeader>
          <DialogTitle>{defaultTitle}</DialogTitle>
          <DialogDescription>{defaultDescription}</DialogDescription>
        </DialogHeader>

        <DialogBody className="min-h-40">
          <UIImageUpload
            onImageSelect={(url) => { handleImageSelect(url); handleMaybeCloseOnRemove(url); }}
            onCropApplied={handleCropApplied}
            currentImage={currentImage}
            currentOriginalImage={currentOriginalImage}
            enableCrop={enableCrop}
            buttonText="Select Image"
            imageCategory={imageCategory}
            projectInstanceId={projectInstanceId}
            sceneOriginId={sceneOriginId}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
